/* React */
import type { RefObject } from "react";

/* d3.js */
import * as d3 from "d3";

/* Helper */
import {
  createXScale,
  createYScaleSurvival,
  drawAxes,
  setTooltipData,
} from "src/components/charts/survival-plot/helper";

/* Utils */
import {
  computePlotHeightWidth,
  createViewBox,
} from "src/utils/helper/charts/helper";

import { getCohortGroupColor } from "src/utils/helper/charts/colorMapper";

import {
  createChartTooltip,
  ScssProps,
} from "src/utils/helper/chart-tooltip";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Interfaces */
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { SurvivalPlotProps } from "src/shared/interfaces/charts/SurvivalPlot";
import { TableProps } from "src/shared/interfaces/charts/Table";
import {
  ChartTooltipRef,
  SurvivalPlotTooltipProps,
} from "src/shared/interfaces/ChartTooltip";

export const createSurvivalPlot = (
  svg: SVGSelection,
  tooltipRef: React.RefObject<ChartTooltipRef>,
  data: SurvivalPlotProps[],
  table: TableProps[],
  dimensions: ChartDimensionsProps,
  showConfidenceIntervals: boolean
) => {
  if (!data) return;
  createViewBox(svg, dimensions);

  const { margin, height, width } = computePlotHeightWidth(
    dimensions,
    { top: 62, right: 92, bottom: 32, left: 92 }
  );

  const allXValues = data.flatMap((d) => d.trace.x ?? []);
  const xScale = createXScale(allXValues, width);
  const yScale = createYScaleSurvival(height);

  const tickValues = Array.from(
    new Set(
      table.flatMap((t) => t.data?.map((d) => d.timeline))
    )
  )
    .filter((v): v is number => typeof v === "number")
    .sort((a, b) => a - b);

  // Define Clip-Path for the plot area
  svg
    .append("defs")
    .append("clipPath")
    .attr("id", "clip-survival-plot")
    .append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", width)
    .attr("height", height);

  const g = svg
    .append("g")
    .attr(
      "transform",
      `translate(${margin.left},${margin.top})`
    );

  // Draw axes
  drawAxes(g, xScale, yScale, tickValues, height, width);

  const gPlot = g
    .append("g")
    .attr("clip-path", "url(#clip-survival-plot)");

  // Plot data
  data.forEach((serie, index) => {
    // Name-aware so the "Not looked for" / "Looked for, no value" cohort
    // curves take the reserved grey and match the legend colours, which use
    // the same helper over the same (filtered, ordered) group list.
    const color = getCohortGroupColor(serie.name, index);

    // Line
    const dataPoints = serie.trace.x.map((xVal, i) => ({
      x: xVal,
      y: serie.trace.y[i],
    }));

    const lineGenerator = d3
      .line<{ x: number; y: number }>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y))
      .curve(d3.curveStepAfter);

    gPlot
      .append("path")
      .datum(dataPoints)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 2)
      .attr("d", lineGenerator);

    // Confidence intervals
    if (
      showConfidenceIntervals &&
      serie.ciLower &&
      serie.ciUpper
    ) {
      const ciPoints = serie.ciLower!.x.map((xVal, i) => ({
        x: xVal,
        y0: serie.ciLower!.y[i],
        y1: serie.ciUpper!.y[i],
      }));

      const areaGenerator = d3
        .area<{ x: number; y0: number; y1: number }>()
        .x((d) => xScale(d.x))
        .y0((d) => yScale(d.y0))
        .y1((d) => yScale(d.y1))
        .curve(d3.curveStepAfter);

      gPlot
        .append("path")
        .datum(ciPoints)
        .attr(
          "fill",
          d3
            .color(color)
            ?.copy({ opacity: 0.2 })
            ?.toString() ?? "rgba(31, 119, 180, 0.2)"
        )
        .attr("d", areaGenerator);
    }

    /**
     * Create a transparent line to make tooltip
     * easier accessible.
     */
    gPlot
      .append("path")
      .datum(dataPoints)
      .attr("fill", "none")
      .attr("stroke", "transparent")
      .attr("stroke-width", 8)
      .attr("d", lineGenerator)
      .style("cursor", "pointer")
      .style("pointer-events", "stroke")
      .on("mousemove", (event: MouseEvent) => {
        const [mouseX] = d3.pointer(event);
        const xVal = xScale.invert(mouseX);

        const bisect = d3.bisector(
          (d: { x: number }) => d.x
        ).center;

        const i = bisect(dataPoints, xVal);
        const point = dataPoints[i];

        const tooltipData: SurvivalPlotTooltipProps = {
          label: serie.name ?? "",
          color,
          time: `${xVal.toFixed(0)} mth`,
          event: point!.y.toFixed(2),
        };

        const scssProps: ScssProps = { margin: -48 };
        createChartTooltip(
          tooltipRef,
          () => setTooltipData(tooltipData),
          svg,
          event,
          scssProps
        );
      })
      .on("mouseleave", () => {
        tooltipRef.current?.hideTooltip();
      });

    // Censored data
    if (serie.censored) {
      const censoredPoints = serie.censored.x.map(
        (xVal, i) => ({
          x: xVal,
          y: serie.censored!.y[i],
        })
      );

      g.selectAll(`.censored-point-${index}`)
        .data(censoredPoints)
        .enter()
        .append("line")
        .attr("x1", (d) => xScale(d.x))
        .attr("x2", (d) => xScale(d.x))
        .attr("y1", (d) => yScale(d.y) - 5)
        .attr("y2", (d) => yScale(d.y) + 5)
        .attr("stroke", color)
        .attr("stroke-width", 1)
        .style("cursor", "pointer")
        .on("mousemove", (event: MouseEvent, d) => {
          const target = event.currentTarget as SVGElement;
          d3.select(target).attr("opacity", 0.85);

          const tooltipData: SurvivalPlotTooltipProps = {
            label: serie.name ?? "",
            color: color,
            time: `${d.x} mth`,
            event: d.y.toFixed(2),
          };

          const scssProps: ScssProps = { margin: -48 };

          createChartTooltip(
            tooltipRef,
            () => setTooltipData(tooltipData),
            svg,
            event,
            scssProps
          );
        })
        .on("mouseleave", (event: MouseEvent) => {
          const target = event.currentTarget as SVGElement;
          d3.select(target).attr("opacity", 1);
          tooltipRef.current?.hideTooltip();
        });
    }
  });
};
